import type { Message, Project, Session, Marker } from "./types";

export interface ObservabilityStats {
  totalSessions: number;
  totalCostUsd: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  activeProjects: string[];
  recentActivity: Array<{
    slug: string;
    sessionCount: number;
    totalCost: number;
    lastActive: number;
  }>;
  projectBreakdown: Array<{
    projectName: string;
    sessions: number;
    cost: number;
    tokens: number;
  }>;
}

export interface DailySnapshot {
  date: string;
  totalCost: number;
  totalSessions: number;
  totalInputTokens: number;
  totalOutputTokens: number;
}

export type ServiceHealthStatus =
  | "ok"
  | "degraded"
  | "error"
  | "unconfigured"
  | "unknown";

export interface ServiceEndpointRow {
  id: string;
  label: string;
  url: string | null;
  status: ServiceHealthStatus;
  detail?: string;
}

export interface ServicesSettingsPayload {
  generatedAt: number;
  baseUrl: string;
  authProvider: string;
  services: ServiceEndpointRow[];
}

interface McpServerConfig {
  name: string;
  command: string;
  args: string[];
  env?: Record<string, string>;
}

interface McpMetric {
  name: string;
  totalCalls: number;
  errorCount: number;
  avgLatencyMs: number;
  lastSeen: number;
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text();
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed.error === "string") message = parsed.error;
    } catch {
      if (body) message = body;
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export interface MemoryFile {
  filename: string;
  content: string;
}

export interface UploadResult {
  fileId: string;
  path: string;
}

async function uploadReq<T>(url: string, file: File): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(url, {
    credentials: "include",
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const body = await res.text();
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed.error === "string") message = parsed.error;
    } catch {
      if (body) message = body;
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export interface FileTreeNode {
  name: string;
  path: string;
  type: "file" | "dir";
  children?: FileTreeNode[];
}

export interface FileTreePayload {
  root: string;
  entries: FileTreeNode[];
  truncated: boolean;
}

export interface FileContentPayload {
  path: string;
  size: number;
  binary: boolean;
  truncated: boolean;
  content: string;
}

export const api = {
  listProjects: () => req<Project[]>("/api/projects"),
  getFileTree: (params: { sessionId?: string; slug?: string }) => {
    const qs = new URLSearchParams();
    if (params.sessionId) qs.set("sessionId", params.sessionId);
    if (params.slug) qs.set("slug", params.slug);
    return req<FileTreePayload>(`/api/files/tree?${qs.toString()}`);
  },
  getFileContent: (params: { sessionId?: string; slug?: string; path: string }) => {
    const qs = new URLSearchParams();
    if (params.sessionId) qs.set("sessionId", params.sessionId);
    if (params.slug) qs.set("slug", params.slug);
    qs.set("path", params.path);
    return req<FileContentPayload>(`/api/files/content?${qs.toString()}`);
  },
  listSessions: (slug: string) =>
    req<Session[]>(`/api/projects/${encodeURIComponent(slug)}/sessions`),
  getSession: (slug: string, id: string) =>
    req<{ session: Session; messages: Message[] }>(
      `/api/sessions/${id}?slug=${encodeURIComponent(slug)}`,
    ),
  createSession: (slug: string, cwd?: string, permissionMode?: string, model?: string) =>
    req<{ sessionId: string; cwd: string }>(
      `/api/projects/${encodeURIComponent(slug)}/sessions`,
      { method: "POST", body: JSON.stringify({ cwd, permissionMode, model }) },
    ),
  prompt: (id: string, text: string, slug: string, attachments?: Array<{ fileId: string; path: string }>) =>
    req<{ ok: true }>(`/api/sessions/${id}/prompt`, {
      method: "POST",
      body: JSON.stringify({ text, slug, attachments }),
    }),
  uploadFile: (sessionId: string, file: File) =>
    uploadReq<UploadResult>(`/api/sessions/${sessionId}/upload`, file),
  fork: (id: string, slug: string) =>
    req<{ sessionId: string; forkedFrom: string }>(
      `/api/sessions/${id}/fork`,
      { method: "POST", body: JSON.stringify({ slug }) },
    ),
  renameSession: (id: string, title: string) =>
    req<{ ok: true; title: string }>(`/api/sessions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),
  abort: (id: string) =>
    req<void>(`/api/sessions/${id}/abort`, { method: "POST" }),
  dispose: (id: string) =>
    req<void>(`/api/sessions/${id}`, { method: "DELETE" }),
  resolvePermission: (
    id: string,
    requestId: string,
    behavior: "allow" | "deny",
  ) =>
    req<{ ok: true }>(`/api/sessions/${id}/permission`, {
      method: "POST",
      body: JSON.stringify({ request_id: requestId, behavior }),
    }),
  getMemory: (slug: string) =>
    req<MemoryFile[]>(`/api/projects/${encodeURIComponent(slug)}/memory`),
  getState: () =>
    req<{ markers: Record<string, Marker[]>; preferences: Record<string, unknown>; recentCwds: string[] }>("/api/state"),
  updateState: (patch: { markers?: Record<string, Marker[]>; preferences?: Record<string, unknown>; recentCwds?: string[] }) =>
    req<{ markers: Record<string, Marker[]>; preferences: Record<string, unknown>; recentCwds: string[] }>("/api/state", { method: "PATCH", body: JSON.stringify(patch) }),
  searchSessions: (q: string) =>
    req<{ results: Array<{ sessionId: string; slug: string; title: string; cwd: string; snippet: string; mtimeMs: number }> }>(
      `/api/search?q=${encodeURIComponent(q)}`,
    ),
  listOpzeroProjects: () =>
    req<{ localProjects: Array<{ name: string; path: string; hasGit: boolean }>; githubProjects: Array<{ name: string; path: string; hasGit: boolean }> }>("/api/opzero/projects"),
  getMcpServers: () =>
    req<{ servers: McpServerConfig[] }>("/api/mcp/servers"),
  getMcpMetrics: () =>
    req<{ metrics: McpMetric[] }>("/api/mcp/metrics"),
  getObservabilityStats: () =>
    req<ObservabilityStats>("/api/observability/stats"),
  restartServer: () =>
    req<{ ok: true; restarting: true }>("/api/server/restart", { method: "POST" }),
  getServicesSettings: () => req<ServicesSettingsPayload>("/api/settings/services"),
};
